import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import ArticleImage from '@/components/ArticleImage';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Tag, Share2, Facebook, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Article = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/articles/${slug}`);
        setArticle(response.data);
      } catch (error) {
        console.error('Error fetching article:', error);
        setError(t('Article non trouvé', 'Artikel nicht gefunden'));
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug, t]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article?.title_fr,
          url: shareUrl
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  if (error || !article) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-4 font-outfit">
            {error || t('Article non trouvé', 'Artikel nicht gefunden')}
          </h1>
          <Button onClick={() => navigate('/blog')} data-testid="back-to-blog">
            <ArrowLeft className="mr-2 w-4 h-4" />
            {t('Retour au blog', 'Zurück zum Blog')}
          </Button>
        </div>
      </Layout>
    );
  }

  const title = language === 'fr' ? article.title_fr : (article.title_de || article.title_fr);
  const content = language === 'fr' ? article.content_fr : (article.content_de || article.content_fr);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative py-16 md:py-24">
        {article.image_url && (
          <div className="absolute inset-0 z-0">
            <ArticleImage
              src={article.image_url}
              alt={title}
              className="w-full h-full object-cover opacity-10"
              containerClassName="w-full h-full"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 to-slate-50"></div>
          </div>
        )}

        <div className="relative max-w-4xl mx-auto px-6 md:px-12">
          {/* Back button */}
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8 font-medium" data-testid="article-back-link">
            <ArrowLeft className="mr-2 w-4 h-4" />
            {t('Retour au blog', 'Zurück zum Blog')}
          </Link>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className={`px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide ${
              article.category === 'experience' ? 'bg-blue-100 text-blue-600' :
              article.category === 'tips' ? 'bg-yellow-100 text-yellow-700' :
              'bg-slate-100 text-slate-600'
            }`}>
              <Tag className="inline w-3 h-3 mr-1" />
              {article.category === 'experience' ? t('Expérience', 'Erfahrung') :
               article.category === 'tips' ? t('Conseils', 'Tipps') : 'Blog'}
            </span>
            <span className="flex items-center text-slate-500 text-sm">
              <Calendar className="w-4 h-4 mr-2" />
              {formatDate(article.created_at)}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-8 font-outfit" data-testid="article-title">
            {title}
          </h1>
        </div>
      </section>

      {/* Featured Image */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 -mt-8 mb-12">
        <div className="rounded-3xl overflow-hidden shadow-2xl">
          <ArticleImage
            src={article.image_url}
            alt={title}
            className="w-full aspect-video object-cover"
            containerClassName="w-full aspect-video"
          />
        </div>
      </section>

      {/* Content */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-20">
        <article 
          className="prose prose-lg prose-slate max-w-none prose-headings:font-outfit prose-headings:text-slate-900 prose-p:text-slate-700 prose-a:text-blue-600 prose-strong:text-slate-900 prose-img:rounded-xl prose-img:shadow-lg"
          data-testid="article-content"
        >
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 className="text-3xl font-bold mt-8 mb-4 font-outfit" {...props} />,
              h2: ({node, ...props}) => <h2 className="text-2xl font-bold mt-6 mb-3 font-outfit" {...props} />,
              h3: ({node, ...props}) => <h3 className="text-xl font-bold mt-4 mb-2 font-outfit" {...props} />,
              p: ({node, ...props}) => <p className="text-slate-700 leading-relaxed mb-6" {...props} />,
              ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-6 text-slate-700" {...props} />,
              ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-2 mb-6 text-slate-700" {...props} />,
              li: ({node, ...props}) => <li className="text-slate-700" {...props} />,
              blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-blue-600 pl-4 italic text-slate-600 my-6" {...props} />,
              a: ({node, ...props}) => <a className="text-blue-600 hover:text-blue-700 underline" {...props} />,
              img: ({node, ...props}) => <img className="rounded-xl shadow-lg my-6 w-full" {...props} />,
              table: ({node, ...props}) => <div className="overflow-x-auto my-6"><table className="min-w-full border-collapse border border-slate-200 rounded-lg" {...props} /></div>,
              thead: ({node, ...props}) => <thead className="bg-slate-100" {...props} />,
              th: ({node, ...props}) => <th className="border border-slate-200 px-4 py-2 text-left font-semibold text-slate-900 text-sm" {...props} />,
              td: ({node, ...props}) => <td className="border border-slate-200 px-4 py-2 text-slate-700 text-sm" {...props} />,
              tr: ({node, ...props}) => <tr className="even:bg-slate-50" {...props} />,
              input: ({node, ...props}) => {
                if (props.type === 'checkbox') {
                  return <input {...props} className="mr-2 accent-blue-600" disabled />;
                }
                return <input {...props} />;
              },
              hr: ({node, ...props}) => <hr className="my-8 border-slate-200" {...props} />,
              code: ({node, inline, ...props}) => 
                inline ? (
                  <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm text-slate-800" {...props} />
                ) : (
                  <code className="block bg-slate-900 text-slate-100 p-4 rounded-xl overflow-x-auto my-4" {...props} />
                ),
            }}
          >
            {content}
          </ReactMarkdown>
        </article>

        {/* Share */}
        <div className="mt-16 pt-8 border-t border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 font-outfit">
            {t('Partager cet article', 'Diesen Artikel teilen')}
          </h3>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleShare}
              className="rounded-full"
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {t('Partager', 'Teilen')}
            </Button>
            <a 
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-full border border-slate-200 hover:bg-slate-100 transition-colors"
              data-testid="share-facebook"
            >
              <Facebook className="w-4 h-4 mr-2" />
              Facebook
            </a>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-center">
          <Link to="/blog">
            <Button 
              size="lg"
              className="btn-primary rounded-full px-8"
              data-testid="view-more-articles"
            >
              {t('Voir plus d\'articles', 'Mehr Artikel ansehen')}
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Article;
